import { patchOf } from './patch';
import { z } from 'zod';
import { paginationQuerySchema } from './pagination';

export const CHANNEL_TYPES = ['WHATSAPP', 'WEBCHAT', 'EMAIL'] as const;
export type ChannelTypeKey = (typeof CHANNEL_TYPES)[number];
export const CONVERSATION_STATUSES = ['OPEN', 'PENDING', 'RESOLVED'] as const;
export const MESSAGE_STATUSES = ['QUEUED', 'SENT', 'DELIVERED', 'READ', 'FAILED', 'RECEIVED'] as const;
export type MessageStatusKey = (typeof MESSAGE_STATUSES)[number];

/** Janela de atendimento do WhatsApp: texto livre só até 24 h depois da última mensagem do cliente. */
export const WHATSAPP_WINDOW_HOURS = 24;

/** Eventos do Socket.IO do painel (13.7) e do chat da loja (13.6). */
export const INBOX_SOCKET_EVENTS = {
  messageReceived: 'inbox.message_received',
  messageStatus: 'inbox.message_status',
  conversationUpdated: 'inbox.conversation_updated',
  webchatMessage: 'webchat.message',
} as const;

// ---------- Mensagens automáticas (13.5) ----------
export const INBOX_AUTOMATIONS = ['order_placed_pix', 'payment_confirmed', 'order_shipped', 'fiscal_document'] as const;
export type InboxAutomationKey = (typeof INBOX_AUTOMATIONS)[number];

export const INBOX_AUTOMATION_LABELS: Record<InboxAutomationKey, string> = {
  order_placed_pix: 'Pedido recebido com o código Pix',
  payment_confirmed: 'Pagamento confirmado',
  order_shipped: 'Pedido enviado com o código de rastreio',
  fiscal_document: 'Link da nota fiscal',
};

export interface DefaultTemplate {
  name: string;
  category: 'UTILITY';
  language: 'pt_BR';
  /** Corpo com `{{1}}`, `{{2}}`…; a Meta exige um exemplo para cada variável. */
  body: string;
  example: string[];
}

/**
 * Modelos UTILITY criados no número de cada lojista ao conectar (13.2, passo 4).
 * A Meta recusa corpo que começa ou termina com variável.
 */
export const DEFAULT_TEMPLATES: Record<InboxAutomationKey | 'owner_alert', DefaultTemplate> = {
  order_placed_pix: {
    name: 'pedido_recebido_pix',
    category: 'UTILITY',
    language: 'pt_BR',
    body: 'Olá, {{1}}! Recebemos seu pedido #{{2}} na {{3}}. Para concluir, pague com o Pix copia e cola: {{4}} Assim que o pagamento for confirmado, avisamos por aqui.',
    example: ['Ana', '1024', 'Loja da Ana', '00020126580014br.gov.bcb.pix'],
  },
  payment_confirmed: {
    name: 'pagamento_confirmado',
    category: 'UTILITY',
    language: 'pt_BR',
    body: 'Olá, {{1}}! O pagamento do pedido #{{2}} na {{3}} foi confirmado. Já estamos separando tudo para você.',
    example: ['Ana', '1024', 'Loja da Ana'],
  },
  order_shipped: {
    name: 'pedido_enviado',
    category: 'UTILITY',
    language: 'pt_BR',
    body: 'Olá, {{1}}! Seu pedido #{{2}} na {{3}} foi enviado por {{4}}. Código de rastreio: {{5}}. Qualquer dúvida, é só responder esta mensagem.',
    example: ['Ana', '1024', 'Loja da Ana', 'Correios', 'AA123456789BR'],
  },
  fiscal_document: {
    name: 'nota_fiscal_emitida',
    category: 'UTILITY',
    language: 'pt_BR',
    body: 'Olá, {{1}}! A nota fiscal do seu pedido #{{2}} na {{3}} foi emitida. Baixe por este link: {{4}} Obrigado pela compra!',
    example: ['Ana', '1024', 'Loja da Ana', 'https://alicerce.com.br/nota'],
  },
  owner_alert: {
    name: 'aviso_ao_dono',
    category: 'UTILITY',
    language: 'pt_BR',
    body: 'Aviso do seu painel: {{1}}. Abra o painel para ver os detalhes.',
    example: ['Pedido #1024 recebido'],
  },
};

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use o formato HH:MM.');

/** Horário de atendimento: dias da semana (0 = domingo) com abertura e fechamento. */
export const businessHoursSchema = z.object({
  timezone: z.string().default('America/Sao_Paulo'),
  days: z
    .array(z.object({ day: z.number().int().min(0).max(6), open: hhmm, close: hhmm }))
    .max(14)
    .default([]),
});
export type BusinessHours = z.infer<typeof businessHoursSchema>;

/** `channels.settings`: automações ligadas, horário e mensagem de ausência. */
export const channelSettingsSchema = z.object({
  automations: z.object(Object.fromEntries(INBOX_AUTOMATIONS.map((k) => [k, z.boolean()])) as Record<InboxAutomationKey, z.ZodBoolean>).partial().default({}),
  businessHours: businessHoursSchema.nullable().default(null),
  awayMessage: z.string().trim().min(1).max(1000).nullable().default(null),
});
export type ChannelSettings = z.infer<typeof channelSettingsSchema>;
export const updateChannelSettingsSchema = patchOf(channelSettingsSchema);

/** Resultado do Embedded Signup (13.2): o front envia o `code` e os ids escolhidos. */
export const connectWhatsappSchema = z.object({
  code: z.string().trim().min(1).max(2000),
  wabaId: z.string().trim().regex(/^\d{5,25}$/, 'Id da conta do WhatsApp inválido.'),
  phoneNumberId: z.string().trim().regex(/^\d{5,25}$/, 'Id do número inválido.'),
});
export type ConnectWhatsappInput = z.infer<typeof connectWhatsappSchema>;

export const CONVERSATION_FILTERS = ['all', 'mine', 'unassigned', 'open', 'resolved'] as const;

export const listConversationsQuerySchema = paginationQuerySchema.extend({
  filter: z.enum(CONVERSATION_FILTERS).default('open'),
  channel: z.enum(CHANNEL_TYPES).optional(),
  tag: z.string().trim().max(40).optional(),
  q: z.string().trim().max(100).optional(),
});
export type ListConversationsQuery = z.infer<typeof listConversationsQuerySchema>;

export const listMessagesQuerySchema = paginationQuerySchema.extend({ limit: z.coerce.number().int().min(1).max(100).default(50) });

const templateParam = z.string().trim().min(1).max(500);

export const sendMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('text'), body: z.string().trim().min(1).max(4096) }),
  z.object({
    type: z.literal('template'),
    templateName: z.string().trim().min(1).max(512),
    language: z.string().trim().min(2).max(10).default('pt_BR'),
    params: z.array(templateParam).max(20).default([]),
  }),
  /** Produto do catálogo como cartão (texto com nome, preço e link no WhatsApp). */
  z.object({ type: z.literal('product'), productId: z.string().uuid(), note: z.string().trim().max(500).optional() }),
]);
export type SendMessageInput = z.infer<typeof sendMessageSchema>;

export const updateConversationSchema = z
  .object({
    status: z.enum(CONVERSATION_STATUSES),
    assigneeUserId: z.string().uuid().nullable(),
    tags: z.array(z.string().trim().min(1).max(40)).max(20),
    orderId: z.string().uuid().nullable(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo.');
export type UpdateConversationInput = z.infer<typeof updateConversationSchema>;

export const quickReplySchema = z.object({
  shortcut: z
    .string()
    .trim()
    .toLowerCase()
    .transform((s) => s.replace(/^\//, ''))
    .pipe(z.string().regex(/^[a-z0-9_-]{1,30}$/, 'Use letras, números, - ou _ (até 30).')),
  body: z.string().trim().min(1).max(4096),
});
export type QuickReplyInput = z.infer<typeof quickReplySchema>;

export const createTemplateSchema = z.object({
  name: z
    .string()
    .trim()
    .regex(/^[a-z0-9_]{1,512}$/, 'Use letras minúsculas, números e _.'),
  category: z.enum(['UTILITY', 'MARKETING']),
  language: z.string().trim().min(2).max(10).default('pt_BR'),
  body: z.string().trim().min(1).max(1024),
  example: z.array(templateParam).max(20).default([]),
});
export type CreateTemplateInput = z.infer<typeof createTemplateSchema>;

// ---------- Chat da loja (público) ----------
export const webchatMessageSchema = z.object({ body: z.string().trim().min(1).max(2000) });

export const webchatIdentifySchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    phone: z.string().trim().max(30).optional(),
    email: z.string().trim().email('E-mail inválido.').max(200).optional(),
  })
  .refine((v) => !!v.phone || !!v.email, { message: 'Informe telefone ou e-mail.', path: ['phone'] });
export type WebchatIdentifyInput = z.infer<typeof webchatIdentifySchema>;

/** Placeholders `{{n}}` de um corpo de modelo, em ordem. */
export function templateVariables(body: string): number[] {
  return [...new Set([...body.matchAll(/\{\{(\d+)\}\}/g)].map((m) => Number(m[1])))].sort((a, b) => a - b);
}

/** Preenche `{{n}}` com os parâmetros (para mostrar no painel o texto enviado). */
export function renderTemplate(body: string, params: string[]): string {
  return body.replace(/\{\{(\d+)\}\}/g, (all, n: string) => params[Number(n) - 1] ?? all);
}
